'use client';

import { useNavigation } from '@contexts/NavigationContext';
import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { restoreScrollPos } from './storage';
import { useContainerScrollRestoration } from './container';
import type { ContainerScrollOptions } from './container';

export function useScrollRestoration(isLoaded?: boolean): void;
export function useScrollRestoration(options: ContainerScrollOptions): {
  restoringRef: RefObject<boolean>;
};
export function useScrollRestoration(
  options?: boolean | ContainerScrollOptions
) {
  const containerOptions = typeof options === 'object' ? options : undefined;
  const isContainer = containerOptions !== undefined;
  const isLoaded = typeof options === 'boolean' ? options : undefined;
  const [isMounted, setMount] = useState(false);
  const { navigationStatus } = useNavigation();
  const containerRestoration = useContainerScrollRestoration(
    containerOptions,
    navigationStatus === 'back'
  );

  // ✅ 호출 시점만 조절: double rAF + 한 번 더 보정 호출
  const scheduleRestore = (force = false) => {
    const key = `${window.location.pathname}`;

    const callOnce = () => restoreScrollPos(key, true);
    const callWithDoubleRaf = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          callOnce();
          // iOS/Safari/가상리스트 대비: 한 프레임 더 보정
          requestAnimationFrame(() => callOnce());
        })
      );

    if (force) {
      // BFCache 등 이미 그려진 상태일 가능성 → 즉시 + 보정 한 번
      callOnce();
      requestAnimationFrame(() => callOnce());
    } else {
      callWithDoubleRaf();
    }
  };

  const pageShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      // bfcache 복귀: 이미 렌더된 경우가 많음 → 즉시 복원 + 보정
      scheduleRestore(true);
    }
    if (
      !e.persisted &&
      !!isLoaded &&
      isMounted &&
      navigationStatus === 'back'
    ) {
      // 일반 진입 + 뒤로가기: 레이아웃 안정화 프레임 뒤 복원
      scheduleRestore(false);
    }
  };

  useEffect(() => {
    if (isContainer) return undefined;
    window.addEventListener('pageshow', pageShow);
    return () => {
      window.removeEventListener('pageshow', pageShow);
    };
    // ✅ isLoaded도 deps에 포함(클로저에 캡처되므로)
  }, [navigationStatus, isMounted, isLoaded, isContainer]);

  useEffect(() => {
    if (isContainer) return;
    if (!!isLoaded && isMounted && navigationStatus === 'back') {
      // 데이터가 늦게 로드된 경우 재시도 타이밍만 조정
      scheduleRestore(false);
    }
  }, [isLoaded, isMounted, navigationStatus, isContainer]);

  useEffect(() => {
    if (isContainer) return;
    if (!('scrollRestoration' in window.history)) return;
    setMount(true);
    window.history.scrollRestoration = 'manual';
  }, [isContainer]);

  return containerOptions ? containerRestoration : undefined;
}
